import { Box } from '@mui/material';
import { React, moment } from 'Imports';
import CalendarNavigation from './CalendarNavigation';
import PresetDateRangeButtons from './PresetDateRangeButtons';
import DateRangeSummary from './DateRangeSummary';
import { DateRange } from 'react-date-range';
import { isEncompass } from '$Utilities/integrationPlatform';
import { toDate, normalizeToStartOfDay } from '$Utilities/dateUtility';
import { useEffect, useState } from 'react';

const styles = isEncompass
    ? (require('./DatePickerCalendar_Encompass.scss') as {
          container: string;
          calendar: string;
      })
    : (require('./DatePickerCalendar_Geotab.scss') as {
          container: string;
          calendar: string;
      });

interface RangeChangeEvent {
    selection: {
        startDate: Date;
        endDate: Date;
        key: string;
    };
}

interface IDatePickerCalendarProps {
    dateRange: {
        startDate: Date | moment.Moment;
        endDate: Date | moment.Moment;
    };
    onSelectedDateRangeChange: (startDate: Date, endDate: Date) => void;
    maxDays?: number;
}

export const DatePickerCalendar: React.FC<IDatePickerCalendarProps> = ({ dateRange, onSelectedDateRangeChange, maxDays }) => {
    // Convert dates to Date objects to ensure compatibility
    const initialStartDate = normalizeToStartOfDay(toDate(dateRange.startDate));
    const initialEndDate = normalizeToStartOfDay(toDate(dateRange.endDate));

    const [startDate, setStartDate] = useState<Date>(initialStartDate);
    const [endDate, setEndDate] = useState<Date>(initialEndDate);

    const dayCount = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const [selectedDaysCount, setSelectedDaysCount] = useState(dayCount);

    // Update selectedDaysCount whenever dateRange changes
    useEffect(() => {
        const currentStartDate = normalizeToStartOfDay(toDate(dateRange.startDate));
        const currentEndDate = normalizeToStartOfDay(toDate(dateRange.endDate));
        const currentDayCount = Math.round((currentEndDate.getTime() - currentStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        setSelectedDaysCount(currentDayCount);
    }, [dateRange.startDate, dateRange.endDate]);

    // Calculate min and max dates based on maxDays prop
    const today = new Date();
    const maxDate = today;
    const minDate = maxDays
        ? (() => {
              const min = new Date(today);
              min.setDate(today.getDate() - maxDays);
              return min;
          })()
        : undefined;

    const handlePresetSelection = (startDate: Date, endDate: Date) => {
        const normalizedStartDate = normalizeToStartOfDay(startDate);
        const normalizedEndDate = normalizeToStartOfDay(endDate);
        onSelectedDateRangeChange(normalizedStartDate, normalizedEndDate);

        // Update selected days count
        const newDayCount = Math.round((normalizedEndDate.getTime() - normalizedStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        setSelectedDaysCount(newDayCount);
    };

    // Custom navigatorRenderer for react-date-range Calendar
    const customNavigatorRenderer = (focusedDate: Date, changeShownDate: (date: Date) => void) => (
        <CalendarNavigation
            currentMonth={focusedDate.getMonth()}
            currentYear={focusedDate.getFullYear()}
            onMonthChange={(month) => {
                const newDate = new Date(focusedDate);
                newDate.setMonth(month);
                changeShownDate(newDate);
            }}
            onYearChange={(year) => {
                const newDate = new Date(focusedDate);
                newDate.setFullYear(year);
                changeShownDate(newDate);
            }}
            onPreviousMonth={() => {
                const newDate = new Date(focusedDate);
                newDate.setMonth(focusedDate.getMonth() - 1);
                changeShownDate(newDate);
            }}
            onNextMonth={() => {
                const newDate = new Date(focusedDate);
                newDate.setMonth(focusedDate.getMonth() + 1);
                changeShownDate(newDate);
            }}
        />
    );

    const handleRangeChange = (change: RangeChangeEvent) => {
        const { startDate, endDate } = change.selection;

        const normalizedStartDate = normalizeToStartOfDay(startDate ?? new Date());
        const normalizedEndDate = normalizeToStartOfDay(endDate ?? new Date());

        onSelectedDateRangeChange(normalizedStartDate, normalizedEndDate);
        setStartDate(normalizedStartDate);
        setEndDate(normalizedEndDate);

        // Update selected days count
        const newDayCount = Math.round((normalizedEndDate.getTime() - normalizedStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        setSelectedDaysCount(newDayCount);
    };

    // Calculate disabled dates based on maxDays prop and future dates
    const isDateDisabled = (date: Date) => {
        // Always disable future dates
        if (date > new Date()) return true;

        // If maxDays is specified, disable dates outside the allowed range
        if (maxDays && minDate) {
            return date < minDate;
        }

        return false;
    };

    const ranges = [
        {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            key: 'selection',
        },
    ];

    return (
        <Box className={styles.container} data-testid={'date-range-picker-dropdown-container'}>
            <Box
                className={styles.calendar}
                id={'date-range-picker-calendar' + (isEncompass ? '-encompass' : '-geotab')}
                data-testid={'date-range-picker-calendar'}
            >
                <DateRange
                    onChange={handleRangeChange}
                    moveRangeOnFirstSelection={false}
                    retainEndDateOnFirstSelection={true}
                    months={1}
                    ranges={ranges}
                    dragSelectionEnabled={false}
                    navigatorRenderer={customNavigatorRenderer}
                    minDate={minDate}
                    maxDate={maxDate}
                    disabledDay={isDateDisabled}
                    showPreview={false}
                />
            </Box>

            <Box
                sx={
                    isEncompass
                        ? { mt: '8px', mb: maxDays === undefined ? '16px' : '' }
                        : { padding: '16px 16px 0px 16px', display: 'flex' }
                }
            >
                <PresetDateRangeButtons
                    dateRange={{
                        startDate: toDate(dateRange.startDate),
                        endDate: toDate(dateRange.endDate),
                    }}
                    onDateRangeChange={handlePresetSelection}
                />
            </Box>

            {maxDays && (
                <Box>
                    <DateRangeSummary selectedDaysCount={selectedDaysCount} maxDays={maxDays} />
                </Box>
            )}
        </Box>
    );
};

export default DatePickerCalendar;
